import React, { useMemo, useState } from 'react';
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Spinner,
} from '../../../components/ui';
import { BVCompetitorSet, BVCompetitorSetDraft, BVIndexedBrand } from '../types';

interface CompetitorSetManagerProps {
  brands: BVIndexedBrand[];
  items: BVCompetitorSet[];
  isLoading?: boolean;
  onCreate: (payload: BVCompetitorSetDraft) => Promise<void>;
  onUpdate: (setId: number, payload: BVCompetitorSetDraft) => Promise<void>;
  onDelete: (setId: number) => Promise<void>;
}

const EMPTY_FORM: BVCompetitorSetDraft = {
  name: '',
  brand_names: [],
  is_default: false,
};

export function CompetitorSetManager({
  brands,
  items,
  isLoading = false,
  onCreate,
  onUpdate,
  onDelete,
}: CompetitorSetManagerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSet, setEditingSet] = useState<BVCompetitorSet | null>(null);
  const [form, setForm] = useState<BVCompetitorSetDraft>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const sortedBrands = useMemo(
    () => [...brands].sort((left, right) => right.count - left.count || left.brand.localeCompare(right.brand)),
    [brands],
  );

  const openCreateModal = () => {
    setEditingSet(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (item: BVCompetitorSet) => {
    setEditingSet(item);
    setForm({
      name: item.name,
      brand_names: item.brand_names,
      is_default: item.is_default,
    });
    setFormError(null);
    setIsModalOpen(true);
  };

  const resetModal = () => {
    setIsModalOpen(false);
    setEditingSet(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }
    resetModal();
  };

  const toggleBrand = (brand: string, checked: boolean) => {
    setForm(current => ({
      ...current,
      brand_names: checked
        ? [...current.brand_names, brand]
        : current.brand_names.filter(item => item !== brand),
    }));
  };

  const handleSubmit = async () => {
    const name = form.name.trim();
    const brandNames = Array.from(new Set(form.brand_names.map(item => item.trim()).filter(Boolean)));

    if (!name) {
      setFormError('Set name is required.');
      return;
    }

    if (brandNames.length === 0) {
      setFormError('Select at least one competitor brand.');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    const payload: BVCompetitorSetDraft = {
      name,
      brand_names: brandNames,
      is_default: form.is_default,
    };

    try {
      if (editingSet) {
        await onUpdate(editingSet.id, payload);
      } else {
        await onCreate(payload);
      }
      resetModal();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Unable to save competitor set.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item: BVCompetitorSet) => {
    if (!window.confirm(`Delete competitor set "${item.name}"?`)) {
      return;
    }

    setDeletingId(item.id);
    try {
      await onDelete(item.id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Panel>
        <PanelHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <PanelTitle>Competitor Sets</PanelTitle>
              <PanelDescription>
                Curate reusable brand groupings for comparative Studio prompts and Insights filters.
              </PanelDescription>
            </div>
            <Button onClick={openCreateModal}>
              <PlusIcon className="mr-2 h-4 w-4" />
              New set
            </Button>
          </div>
        </PanelHeader>
        <PanelBody className="space-y-4">
          {isLoading ? (
            <div className="flex min-h-[160px] items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <Alert variant="info" title="No competitor sets yet">
              Create a tenant-scoped set to give Studio and Insights a safe default comparison basket.
            </Alert>
          ) : (
            items.map(item => (
              <div
                key={item.id}
                className="rounded-2xl border border-gray-200 p-4 dark:border-dark-border"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-charcoal dark:text-gray-100">{item.name}</p>
                      {item.is_default ? <Badge variant="brand">Default</Badge> : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {item.brand_names.map(brand => (
                        <Badge key={brand} variant="secondary">
                          {brand}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditModal(item)}>
                      <PencilSquareIcon className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={deletingId === item.id}
                      onClick={() => void handleDelete(item)}
                    >
                      <TrashIcon className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </PanelBody>
      </Panel>

      <Modal open={isModalOpen} onClose={closeModal}>
        <ModalBackdrop />
        <ModalContent size="lg">
          <ModalHeader>
            <ModalTitle>{editingSet ? 'Edit competitor set' : 'Create competitor set'}</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-5">
            {formError ? (
              <Alert variant="error" title="Unable to save set">
                {formError}
              </Alert>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="bv-competitor-set-name">Set name</Label>
              <Input
                id="bv-competitor-set-name"
                value={form.name}
                onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
                placeholder="Premium audio competitors"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-3">
              <div>
                <Label>Brands</Label>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Select one or more indexed brands to include in this comparison basket.
                </p>
              </div>
              <div className="max-h-72 space-y-2 overflow-y-auto rounded-2xl border border-gray-200 p-3 dark:border-dark-border">
                {sortedBrands.length > 0 ? (
                  sortedBrands.map(brand => {
                    const checked = form.brand_names.includes(brand.brand);
                    return (
                      <div
                        key={brand.brand}
                        className="rounded-xl px-2 py-2 hover:bg-gray-50 dark:hover:bg-dark-surface-2"
                      >
                        <Checkbox
                          checked={checked}
                          onChange={event => toggleBrand(brand.brand, event.target.checked)}
                          disabled={isSubmitting}
                          label={brand.brand}
                          description={`${brand.count.toLocaleString()} reviews`}
                        />
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Indexed brands appear here after ingestion completes.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 px-3 py-3 dark:border-dark-border">
              <Checkbox
                checked={form.is_default}
                onChange={event => setForm(current => ({ ...current, is_default: event.target.checked }))}
                disabled={isSubmitting}
                label="Use as default set"
                description="New Studio drafts will preselect this competitor basket when no override is provided."
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : editingSet ? 'Save changes' : 'Create set'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default CompetitorSetManager;
